import { JSX } from "react";

import { Box, LinearProgress, Alert, useMediaQuery, useTheme, Theme } from "@mui/material";
import { DataGrid, type GridAutosizeOptions, type GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { mobileColumnVisibilityModel } from "./filesGrid.helper";
import { isFolderItem, type DataItem } from "../../interfaces/DataItem";

const autosizeOptions: GridAutosizeOptions = {
  expand: true,
};

const getStyles = (theme: Theme) => ({
  box: {
    border: 1,
    borderColor: theme.palette.border.default,
    backgroundColor: theme.palette.background.paper,
    height: "75vh",
    width: "100%",
  },
});

function FilesGrid({
  data,
  columns,
  isLoading,
  error,
}: {
  data: DataItem[];
  columns: GridColDef[];
  isLoading: boolean;
  error: Error | null;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const columnVisibilityModel = isDesktop ? {} : mobileColumnVisibilityModel;

  return (
    <>
      {error && <Alert severity='error'>{t("There was an error while fetching files")}</Alert>}
      {isLoading && <LinearProgress color='primary' />}
      <Box sx={getStyles(theme).box}>
        <DataGrid
          rowHeight={52}
          rows={data}
          autosizeOnMount
          autosizeOptions={autosizeOptions}
          columnVisibilityModel={columnVisibilityModel}
          columns={columns}
          scrollbarSize={0}
          getRowId={(row) => row.id}
          hideFooter
          onRowClick={(params) => {
            if (isFolderItem(params.row)) {
              navigate(`/files/${params.row.id}`);
            }
          }}
        />
      </Box>
    </>
  );
}

export default FilesGrid;
